/**
 * Entities — instances of a given Blueprint.
 *
 * URL: /app/entities/:blueprintId
 * Auto-renders columns from the blueprint's schema and a form for create/edit.
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  Plus, RefreshCw, Save, Trash2, X, ChevronLeft, AlertCircle,
  CheckCircle2, Circle, Award, Shield,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Property {
  name: string
  title?: string
  type: string
  enum?: string[] | null
  required?: boolean
  default?: any
}

interface Blueprint {
  identifier: string
  title: string
  schema: Property[]
}

interface Entity {
  id: number
  blueprint_identifier: string
  identifier: string
  title: string
  properties: Record<string, any>
  created_at: string
  updated_at: string
}

interface ScorecardSummary {
  scorecard_identifier: string
  level: string
  score: number
}

const LEVEL_COLOR: Record<string, string> = {
  gold: 'bg-amber/20 text-amber border-amber/40',
  silver: 'bg-whitesmoke/10 text-whitesmoke border-whitesmoke/30',
  bronze: 'bg-orange-500/20 text-orange-400 border-orange-500/40',
  none: 'bg-graphite text-whitesmoke/40 border-gunmetal',
}

const LEVEL_ICON: Record<string, React.ElementType> = {
  gold: Award,
  silver: Award,
  bronze: Award,
  none: Circle,
}

export default function EntitiesPage() {
  const { blueprintId = '' } = useParams()
  const [bp, setBp] = useState<Blueprint | null>(null)
  const [entities, setEntities] = useState<Entity[]>([])
  const [scorecardsByEntity, setScorecardsByEntity] = useState<Record<string, ScorecardSummary[]>>({})
  const [loading, setLoading] = useState(true)
  const [editor, setEditor] = useState<Entity | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    if (!blueprintId) return
    setLoading(true)
    try {
      const [bpRes, entRes] = await Promise.all([
        api.get(`/api/blueprints/${blueprintId}`),
        api.get(`/api/blueprints/${blueprintId}/entities`),
      ])
      setBp(bpRes.data)
      setEntities(entRes.data.entities || [])
      // Fetch grid to get scorecard summaries
      try {
        const { data: grid } = await api.get(`/api/scorecards/grid/${blueprintId}`)
        const map: Record<string, ScorecardSummary[]> = {}
        for (const row of grid.rows || []) {
          map[row.entity_identifier] = row.cells.map((c: any) => ({
            scorecard_identifier: c.scorecard_identifier,
            level: c.level,
            score: c.score,
          }))
        }
        setScorecardsByEntity(map)
      } catch {
        // scorecards optional
      }
      setError(null)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Load failed')
    } finally {
      setLoading(false)
    }
  }, [blueprintId])

  useEffect(() => { load() }, [load])

  const columns = useMemo(() => {
    if (!bp) return []
    return bp.schema.slice(0, 4)  // show first 4 in grid
  }, [bp])

  const newEntity = () => {
    if (!bp) return
    const defaults: Record<string, any> = {}
    for (const p of bp.schema) {
      if (p.default !== undefined) defaults[p.name] = p.default
      else if (p.type === 'boolean') defaults[p.name] = false
      else if (p.type === 'number') defaults[p.name] = 0
      else defaults[p.name] = ''
    }
    setEditor({
      id: 0,
      blueprint_identifier: blueprintId,
      identifier: '',
      title: '',
      properties: defaults,
      created_at: '',
      updated_at: '',
    })
  }

  const edit = (e: Entity) => setEditor({ ...e, properties: { ...e.properties } })

  const save = async () => {
    if (!editor) return
    if (!/^[a-z][a-z0-9_-]{1,199}$/.test(editor.identifier)) {
      setError('entity identifier must be lowercase slug')
      return
    }
    setSaving(true)
    try {
      const payload = {
        identifier: editor.identifier,
        title: editor.title || editor.identifier,
        properties: editor.properties,
        relations: {},
      }
      if (editor.id === 0) {
        await api.post(`/api/blueprints/${blueprintId}/entities`, payload)
      } else {
        await api.put(`/api/entities/${blueprintId}/${editor.identifier}`, payload)
      }
      setEditor(null)
      // Recompute scorecards for this blueprint
      try {
        const scs = await api.get(`/api/scorecards`, { params: { blueprint: blueprintId } })
        for (const sc of scs.data.scorecards || []) {
          await api.post(`/api/scorecards/${sc.identifier}/compute`)
        }
      } catch { /* optional */ }
      await load()
      setError(null)
    } catch (e: any) {
      const d = e?.response?.data?.detail
      setError(typeof d === 'string' ? d : d?.errors?.join('; ') || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const deleteEntity = async (e: Entity) => {
    if (!confirm(`Delete ${e.identifier}?`)) return
    try {
      await api.delete(`/api/entities/${blueprintId}/${e.identifier}`)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Delete failed')
    }
  }

  const renderCell = (p: Property, val: any) => {
    if (val === undefined || val === null || val === '') return <span className="text-whitesmoke/30">—</span>
    if (p.type === 'boolean') {
      return val ? <CheckCircle2 className="w-4 h-4 text-emerald" /> : <Circle className="w-4 h-4 text-whitesmoke/30" />
    }
    if (p.type === 'url') return <a href={String(val)} target="_blank" rel="noreferrer" className="text-sky hover:underline text-xs">link</a>
    if (p.type === 'number') return <span className="font-mono">{val}</span>
    return <span className="truncate">{String(val)}</span>
  }

  const renderInput = (p: Property, val: any, onChange: (v: any) => void) => {
    if (p.type === 'boolean') {
      return (
        <input
          type="checkbox"
          checked={!!val}
          onChange={e => onChange(e.target.checked)}
        />
      )
    }
    if (p.type === 'select' && p.enum) {
      return (
        <select
          value={val ?? ''}
          onChange={e => onChange(e.target.value)}
          className="w-full px-2 py-1.5 bg-graphite border border-gunmetal rounded text-sm"
        >
          <option value="">—</option>
          {p.enum.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      )
    }
    if (p.type === 'number') {
      return (
        <input
          type="number"
          value={val ?? ''}
          onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className="w-full px-2 py-1.5 bg-graphite border border-gunmetal rounded text-sm font-mono"
        />
      )
    }
    if (p.type === 'array') {
      return (
        <input
          value={Array.isArray(val) ? val.join(',') : ''}
          onChange={e => onChange(e.target.value ? e.target.value.split(',').map(s => s.trim()) : [])}
          placeholder="comma,separated,values"
          className="w-full px-2 py-1.5 bg-graphite border border-gunmetal rounded text-sm"
        />
      )
    }
    return (
      <input
        type={p.type === 'date' ? 'date' : (p.type === 'email' ? 'email' : 'text')}
        value={val ?? ''}
        onChange={e => onChange(e.target.value)}
        className="w-full px-2 py-1.5 bg-graphite border border-gunmetal rounded text-sm"
      />
    )
  }

  return (
    <div className="min-h-screen bg-graphite text-whitesmoke">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gunmetal">
          <div>
            <Link to="/app/blueprints" className="text-xs text-sky hover:underline flex items-center gap-1">
              <ChevronLeft className="w-3 h-3" /> Blueprints
            </Link>
            <h1 className="text-2xl font-semibold mt-1">
              {bp?.title || blueprintId}
            </h1>
            <p className="text-sm text-whitesmoke/60 mt-0.5">
              {entities.length} {entities.length === 1 ? 'entity' : 'entities'} · {bp?.schema.length ?? 0} properties
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              className="px-3 py-1.5 rounded bg-gunmetal hover:bg-gunmetal/80 text-sm flex items-center gap-1.5"
            >
              <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} />
              Refresh
            </button>
            <button
              onClick={newEntity}
              className="px-3 py-1.5 rounded bg-sky/20 hover:bg-sky/30 text-sky text-sm flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              New entity
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-rose/10 border border-rose/30 text-rose text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="border border-gunmetal rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gunmetal/40 text-xs uppercase tracking-wider text-whitesmoke/60">
              <tr>
                <th className="text-left px-3 py-2 font-normal">Identifier</th>
                <th className="text-left px-3 py-2 font-normal">Title</th>
                {columns.map(c => (
                  <th key={c.name} className="text-left px-3 py-2 font-normal">{c.title || c.name}</th>
                ))}
                <th className="text-left px-3 py-2 font-normal">Scorecards</th>
                <th className="text-right px-3 py-2 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {entities.map(e => (
                <tr key={e.id} className="border-t border-gunmetal/50 hover:bg-gunmetal/20">
                  <td className="px-3 py-2 font-mono text-xs">{e.identifier}</td>
                  <td className="px-3 py-2 font-medium">{e.title}</td>
                  {columns.map(c => (
                    <td key={c.name} className="px-3 py-2">{renderCell(c, e.properties?.[c.name])}</td>
                  ))}
                  <td className="px-3 py-2">
                    <div className="flex gap-1 flex-wrap">
                      {(scorecardsByEntity[e.identifier] || []).map(s => {
                        const Icon = LEVEL_ICON[s.level] || Circle
                        return (
                          <span
                            key={s.scorecard_identifier}
                            title={`${s.scorecard_identifier}: ${s.level} (${s.score})`}
                            className={clsx(
                              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px]',
                              LEVEL_COLOR[s.level],
                            )}
                          >
                            <Icon className="w-2.5 h-2.5" />
                            {s.score}
                          </span>
                        )
                      })}
                      {(scorecardsByEntity[e.identifier] || []).length === 0 && (
                        <span className="text-xs text-whitesmoke/30">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => edit(e)}
                      className="text-xs text-sky hover:underline mr-3"
                    >edit</button>
                    <button
                      onClick={() => deleteEntity(e)}
                      className="text-xs text-rose hover:underline"
                    >delete</button>
                  </td>
                </tr>
              ))}
              {entities.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 4} className="py-12 text-center text-whitesmoke/40">
                    No entities yet. Click <strong className="text-whitesmoke/70">New entity</strong> to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal editor */}
      {editor && bp && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setEditor(null) }}
        >
          <div className="bg-graphite border border-gunmetal rounded max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gunmetal/40 px-4 py-3 flex items-center justify-between sticky top-0">
              <div className="text-sm uppercase tracking-wider text-whitesmoke/70">
                {editor.id === 0 ? 'New' : 'Edit'} {bp.title.toLowerCase()}
              </div>
              <button onClick={() => setEditor(null)} className="text-whitesmoke/60 hover:text-whitesmoke">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs text-whitesmoke/60">Identifier (slug)</span>
                  <input
                    value={editor.identifier}
                    onChange={e => setEditor({ ...editor, identifier: e.target.value })}
                    disabled={editor.id !== 0}
                    className="w-full mt-1 px-2 py-1.5 bg-gunmetal/30 border border-gunmetal rounded text-sm font-mono"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-whitesmoke/60">Title</span>
                  <input
                    value={editor.title}
                    onChange={e => setEditor({ ...editor, title: e.target.value })}
                    className="w-full mt-1 px-2 py-1.5 bg-gunmetal/30 border border-gunmetal rounded text-sm"
                  />
                </label>
              </div>
              <div className="border-t border-gunmetal pt-3">
                <div className="text-xs uppercase tracking-wider text-whitesmoke/70 mb-2">Properties</div>
                <div className="grid grid-cols-2 gap-3">
                  {bp.schema.map(p => (
                    <label key={p.name} className="block">
                      <span className="text-xs text-whitesmoke/60 flex items-center gap-1">
                        {p.title || p.name}
                        {p.required && <span className="text-rose">*</span>}
                        <span className="text-whitesmoke/30 text-[10px] font-mono">({p.type})</span>
                      </span>
                      <div className="mt-1">
                        {renderInput(
                          p,
                          editor.properties[p.name],
                          v => setEditor({ ...editor, properties: { ...editor.properties, [p.name]: v } }),
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setEditor(null)}
                  className="px-3 py-1.5 rounded bg-gunmetal hover:bg-gunmetal/70 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-3 py-1.5 rounded bg-emerald/20 hover:bg-emerald/30 text-emerald text-sm flex items-center gap-1.5"
                >
                  <Save className="w-3.5 h-3.5" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
